import type { RosterEntry } from 'components/IAP/hooks/incidentRoster/incidentRoster.types';
import type { Ics204SelectOption } from './Ics204Fields';

/**
 * Fixed section-4 positions — never use roster seat titles like Incident
 * Commander.
 */
export const PERSONNEL_ROLES = [
  {
    position: 'Section Chief',
    nameField: 'sectionChief',
    contactField: 'sectionChiefContact',
    nameTestId: 'ics204-edit-section-chief',
    contactTestId: 'ics204-edit-section-chief-contact',
    viewNameTestId: 'ics204-field-section-chief',
    viewContactTestId: 'ics204-field-section-chief-contact',
  },
  {
    position: 'Branch Director',
    nameField: 'branchDirector',
    contactField: 'branchDirectorContact',
    nameTestId: 'ics204-edit-branch-director',
    contactTestId: 'ics204-edit-branch-director-contact',
    viewNameTestId: 'ics204-field-branch-director',
    viewContactTestId: 'ics204-field-branch-director-contact',
  },
  {
    position: 'Division/Group Supervisor',
    nameField: 'divisionGroupSupervisor',
    contactField: 'divisionGroupSupervisorContact',
    nameTestId: 'ics204-edit-division-supervisor',
    contactTestId: 'ics204-edit-division-supervisor-contact',
    viewNameTestId: 'ics204-field-division-supervisor',
    viewContactTestId: 'ics204-field-division-supervisor-contact',
  },
] as const;

export type PersonnelNameField = (typeof PERSONNEL_ROLES)[number]['nameField'];
export type PersonnelContactField =
  (typeof PERSONNEL_ROLES)[number]['contactField'];

/** Prefer mobile, then email — matches what operators expect on the 204. */
export const contactFromRosterEntry = (entry: RosterEntry): string => {
  const mobile = entry.contact?.mobilePhone?.trim();
  if (mobile) return mobile;
  return entry.emailAddress?.trim() || '';
};

/**
 * Unique person names from active + draft roster, plus any values already
 * stored on the card so clears/departures do not drop the current selection.
 */
export const buildRosterNameOptions = (
  activeRoster: RosterEntry[] | undefined,
  draftRoster: RosterEntry[] | undefined,
  preservedNames: Array<string | null | undefined>
): Ics204SelectOption[] => {
  const names = new Set<string>();
  for (const entry of [...(activeRoster ?? []), ...(draftRoster ?? [])]) {
    if (entry.userName?.trim()) names.add(entry.userName.trim());
  }
  for (const name of preservedNames) {
    if (name?.trim()) names.add(name.trim());
  }
  return [...names]
    .sort((a, b) => a.localeCompare(b))
    .map((name) => ({ value: name, label: name }));
};

/** Lookup contact for a selected display name across active then draft roster. */
export const findRosterContactForName = (
  name: string,
  activeRoster: RosterEntry[] | undefined,
  draftRoster: RosterEntry[] | undefined
): string => {
  if (!name.trim()) return '';
  const match = [...(activeRoster ?? []), ...(draftRoster ?? [])].find(
    (entry) => entry.userName?.trim() === name.trim()
  );
  return match ? contactFromRosterEntry(match) : '';
};
